import { API_BASE } from "../config";

// =============================================================================
// File: frontend/src/components/NutritionComparisonMode.jsx
// Sprint 2 — Nutrition Comparison Mode - Side-by-Side Product Analysis UI
// CSS EXTRACTED — all styles in healist-app-components.css (ncm- prefix)
// REWORKED: 100% UPC-driven flow — no name-based comparison
// SCANNER: Self-contained STRICH SDK scanner (same pattern as VisionGroceryCapture)
//          Mobile: UPC input + Scan button below. Desktop: UPC input only.
// Patent claims exercised: P-003 Claims 15-17 | P-005 Claims 9-10
// =============================================================================

import React, { useState, useEffect, useRef } from 'react';
import MedicalDisclaimer from './MedicalDisclaimer';
import EdamamAttribution from './EdamamAttribution';
import { useBarcodeScanner } from '../hooks/useBarcodeScanner';
import { useConfirm } from './ConfirmProvider';

const NutritionComparisonMode = ({ token, fetchWithAuth: fetchWithAuthProp, onClose, initialProductA = null, initialProductB = null }) => {
  const confirm = useConfirm();
  // State
  const [productA, setProductA] = useState(initialProductA || { name: '', upc: '', brand: '', nutrition: {} });
  const [productB, setProductB] = useState(initialProductB || { name: '', upc: '', brand: '', nutrition: {} });
  const [comparisonResult, setComparisonResult] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [activeInput, setActiveInput] = useState('A');

  // Scanner state
  const [scanTarget, setScanTarget] = useState(null); // 'A' or 'B' — which product card opened scanner
  const [isScanning, setIsScanning] = useState(false);
  const [scannerError, setScannerError] = useState('');
  const hostElementRef = useRef(null);
  const scannedBarcodesRef = useRef(new Set());
  const scanTargetRef = useRef(null); // Ref mirror of scanTarget for use inside async callbacks

  // STRICH SDK hook — single source of truth
  const {
    sdkReady,
    error: sdkHookError,
    isMobile,
    startCameraScanning,
    stopCameraScanning
  } = useBarcodeScanner({ fetchWithAuth: fetchWithAuthProp });

  // Propagate hook errors
  useEffect(() => {
    if (sdkHookError) setScannerError(sdkHookError);
  }, [sdkHookError]);

  // Open camera scanner for a product card
  const openScanner = (target) => {
    scanTargetRef.current = target;
    setScanTarget(target);
    setScannerError('');
    setIsScanning(true);
  };

  // Effect to start scanner after DOM renders
  useEffect(() => {
    if (!isScanning) return;

    const timer = setTimeout(async () => {
      const success = await startCameraScanning({
        selector: '#ncm-scanner-host',
        onDetection: async (detections) => {
          for (const detection of detections) {
            const barcode = detection.data;
            if (scannedBarcodesRef.current.has(barcode)) continue;
            scannedBarcodesRef.current.add(barcode);
            const target = scanTargetRef.current;
            await closeScanner();
            const setter = target === 'A' ? setProductA : setProductB;
            setter(prev => ({ ...prev, upc: barcode }));
            await lookupUPC(barcode, target);
          }
        }
      });

      if (!success) {
        setScannerError('Failed to start scanner. Check camera permissions.');
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [isScanning, sdkReady]);

  const closeScanner = async () => {
    await stopCameraScanning();
    setIsScanning(false);
    setScanTarget(null);
    scanTargetRef.current = null;
    scannedBarcodesRef.current.clear();
  };

  // Auto-lookup a UPC and populate product card
  const lookupUPC = async (upc, target) => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/products/lookup/upc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ upc })
      });
      if (response.ok) {
        const data = await response.json();
        const product = data.product || data;
        const setter = target === 'A' ? setProductA : setProductB;
        setter(prev => ({
          ...prev,
          upc,
          name: product.label || product.name || '',
          brand: product.brand || '',
          nutrition: product.nutrition || {}
        }));
      }
    } catch (err) {
      console.error('UPC lookup error:', err);
    }
  };

  // Fetch helper — delegates to prop fetchWithAuth (with 401 retry) if available
  const fetchWithAuth = fetchWithAuthProp || (async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    return response;
  });

  // Load comparison history
  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/compare/history?limit=10`);
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      }
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  };

  // Compare by UPC — the ONLY comparison path
  const compareByUPC = async () => {
    if (!productA.upc || !productB.upc) {
      setError('Please enter or scan a barcode for both products');
      return;
    }

    setComparing(true);
    setError('');
    setComparisonResult(null);

    try {
      const response = await fetchWithAuth(
        `${API_BASE}/api/compare/by-upc?upc_a=${productA.upc}&upc_b=${productB.upc}`,
        { method: 'POST' }
      );

      if (response.ok) {
        const result = await response.json();
        setComparisonResult(result);
        // Populate product cards with returned data
        if (result.product_a) {
          setProductA(prev => ({
            ...prev,
            name: result.product_a.name || '',
            brand: result.product_a.brand || '',
            nutrition: result.product_a.nutrition || {}
          }));
        }
        if (result.product_b) {
          setProductB(prev => ({
            ...prev,
            name: result.product_b.name || '',
            brand: result.product_b.brand || '',
            nutrition: result.product_b.nutrition || {}
          }));
        }
        await loadHistory();
      } else {
        const err = await response.json();
        setError(err.detail || 'One or both products not found');
      }
    } catch (err) {
      setError('Network error during comparison');
    } finally {
      setComparing(false);
    }
  };

  // Record user choice
  const recordChoice = async (choice) => {
    if (!comparisonResult?.comparison_id) return;

    try {
      await fetchWithAuth(
        `${API_BASE}/api/compare/history/${comparisonResult.comparison_id}/choice?choice=${choice}`,
        { method: 'POST' }
      );
      setComparisonResult(prev => ({ ...prev, userChoice: choice }));
    } catch (err) {
      console.error('Failed to record choice:', err);
    }
  };

  // Clear and start new comparison
  const resetComparison = () => {
    setProductA({ name: '', upc: '', brand: '', nutrition: {} });
    setProductB({ name: '', upc: '', brand: '', nutrition: {} });
    setComparisonResult(null);
    setError('');
  };

  // Swap products
  const swapProducts = () => {
    const tempA = { ...productA };
    setProductA({ ...productB });
    setProductB(tempA);
    setComparisonResult(null);
  };

  // openScanner is now defined above (self-contained STRICH SDK)

  // Score badge colors (dynamic — must remain inline)
  const getScoreColor = (score) => {
    const colors = {
      'A': { bg: '#D1FAE5', text: '#065F46' },
      'B': { bg: '#D1FAE5', text: '#065F46' },
      'C': { bg: '#FEF3C7', text: '#92400E' },
      'D': { bg: '#FED7AA', text: '#9A3412' },
      'E': { bg: '#FEE2E2', text: '#991B1B' }
    };
    return colors[score] || { bg: '#F3F4F6', text: '#6B7280' };
  };

  // Render product input card — UPC-driven
  const renderProductCard = (product, setProduct, label, isA) => (
    <div
      className={`ncm-productCard${activeInput === label ? ' ncm-productCard--active' : ''}`}
      onClick={() => setActiveInput(label)}
    >
      <div className="ncm-productLabel">
        <span
          className="ncm-productBadge"
          style={{ backgroundColor: isA ? '#1E3A5F' : '#F59E0B' }}
        >{label}</span>
        Product {label}
      </div>

      {/* UPC/Barcode — REQUIRED */}
      <div className="ncm-barcodeRow ncm-barcodeRow--stacked">
        <input
          type="text"
          placeholder="Enter UPC (required)"
          value={product.upc}
          onChange={(e) => {
            const newUpc = e.target.value;
            setProduct(prev => ({
              ...prev,
              upc: newUpc,
              name: '',
              brand: '',
              nutrition: {}
            }));
          }}
          className="ncm-input ncm-barcodeInput"
        />
        {isMobile && (
          <button
            className="ncm-scanBtn"
            onClick={(e) => { e.stopPropagation(); openScanner(label); }}
            disabled={!sdkReady}
          >
            {sdkReady ? 'Scan Barcode' : 'Loading Scanner...'}
          </button>
        )}
      </div>

      {/* Product Info — read-only, populated by lookup */}
      {product.name && (
        <div className="ncm-productInfo">
          <span className="ncm-productName">{product.name}</span>
          {product.brand && <span className="ncm-productBrand">{product.brand}</span>}
        </div>
      )}

      {/* Nutrition preview — read-only, populated by lookup */}
      {product.nutrition && Object.keys(product.nutrition).some(k => product.nutrition[k]) && (
        <>
          <div className="ncm-nutritionLabel">
            Nutrition (per 100g):
          </div>
          <div className="ncm-nutritionGrid">
            {product.nutrition.calories != null && (
              <div className="ncm-nutritionValue">Cal: {product.nutrition.calories}</div>
            )}
            {product.nutrition.protein != null && (
              <div className="ncm-nutritionValue">Protein: {product.nutrition.protein}g</div>
            )}
            {product.nutrition.fat != null && (
              <div className="ncm-nutritionValue">Fat: {product.nutrition.fat}g</div>
            )}
            {product.nutrition.carbs != null && (
              <div className="ncm-nutritionValue">Carbs: {product.nutrition.carbs}g</div>
            )}
          </div>
        </>
      )}
    </div>
  );

  // Render comparison results
  const renderResults = () => {
    if (!comparisonResult) return null;

    const { overall_winner, winner_reason, nutrition_comparison, score_comparison, personalized_recommendation, best_for_you } = comparisonResult;

    return (
      <div className="ncm-results">
        {/* Winner Banner */}
        <div className={`ncm-winnerBanner${overall_winner === 'A' ? ' ncm-winnerBanner--a' : overall_winner === 'B' ? ' ncm-winnerBanner--b' : ' ncm-winnerBanner--tie'}`}>
          <div className="ncm-winnerEmoji">
            {overall_winner === 'A' ? 'A' : overall_winner === 'B' ? 'B' : 'Tie'}
          </div>
          <div className="ncm-winnerTitle">
            {overall_winner ? `Product ${overall_winner} Wins!` : 'Tie - Both are Similar'}
          </div>
          <div className="ncm-winnerReason">
            {winner_reason}
          </div>
        </div>

        {/* Allergen/Condition Alerts */}
        {best_for_you?.alerts?.length > 0 && (
          <div className="ncm-alertBanner">
            {best_for_you.alerts.map((alert, idx) => (
              <div key={idx} className="ncm-alertItem">
                <span className="ncm-alertIcon">⚠</span>
                <span className="ncm-alertText">
                  Product {alert.product}: {alert.detail}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Best For You Summary */}
        {best_for_you && (
          <div className={`ncm-best4uCard${best_for_you.winner === 'A' ? ' ncm-best4uCard--a' : best_for_you.winner === 'B' ? ' ncm-best4uCard--b' : ''}`}>
            <div className="ncm-best4uLabel">Best for You</div>
            <div className="ncm-best4uWinner">
              {best_for_you.winner
                ? `Product ${best_for_you.winner}: ${best_for_you.winner === 'A' ? (comparisonResult.product_a?.name || 'Product A') : (comparisonResult.product_b?.name || 'Product B')}`
                : 'No clear winner for your profile'}
            </div>
            <div className="ncm-best4uReason">{best_for_you.reason}</div>
          </div>
        )}

        {/* No profile data CTA */}
        {!best_for_you && (
          <div className="ncm-best4uEmpty">
            Set up your health profile for personalized recommendations
          </div>
        )}

        {/* Score Comparison */}
        <div className="ncm-scoreRow">
          {score_comparison.nutri_score?.available && (
            <div className="ncm-scoreItem">
              <div className="ncm-scoreLabel">Nutri-Score</div>
              <div className="ncm-scoreValues">
                <span className="ncm-scoreBadge" style={{ backgroundColor: getScoreColor(score_comparison.nutri_score.product_a).bg, color: getScoreColor(score_comparison.nutri_score.product_a).text }}>
                  {score_comparison.nutri_score.product_a || '—'}
                </span>
                <span className="ncm-vsText">vs</span>
                <span className="ncm-scoreBadge" style={{ backgroundColor: getScoreColor(score_comparison.nutri_score.product_b).bg, color: getScoreColor(score_comparison.nutri_score.product_b).text }}>
                  {score_comparison.nutri_score.product_b || '—'}
                </span>
              </div>
            </div>
          )}
          {score_comparison.eco_score?.available && (
            <div className="ncm-scoreItem">
              <div className="ncm-scoreLabel">Eco-Score</div>
              <div className="ncm-scoreValues">
                <span className="ncm-scoreBadge" style={{ backgroundColor: getScoreColor(score_comparison.eco_score.product_a).bg, color: getScoreColor(score_comparison.eco_score.product_a).text }}>
                  {score_comparison.eco_score.product_a || '—'}
                </span>
                <span className="ncm-vsText">vs</span>
                <span className="ncm-scoreBadge" style={{ backgroundColor: getScoreColor(score_comparison.eco_score.product_b).bg, color: getScoreColor(score_comparison.eco_score.product_b).text }}>
                  {score_comparison.eco_score.product_b || '—'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Nutrition Comparison Table */}
        <table className="ncm-table">
          <thead>
            <tr>
              <th className="ncm-th ncm-thMetric">Metric</th>
              <th className="ncm-th ncm-thProductA">
                {comparisonResult.product_a?.name || 'Product A'}
              </th>
              <th className="ncm-th ncm-thProductB">
                {comparisonResult.product_b?.name || 'Product B'}
              </th>
              <th className="ncm-th">Better</th>
              {best_for_you?.per_metric && <th className="ncm-th ncm-thBest4u">Best4U</th>}
            </tr>
          </thead>
          <tbody>
            {nutrition_comparison?.map((item, idx) => (
              <tr key={idx}>
                <td className="ncm-td ncm-tdMetric">
                  {item.metric}
                  <span className="ncm-directionHint">
                    ({item.better_is === 'lower' ? '↓' : '↑'})
                  </span>
                </td>
                <td className={`ncm-td${item.winner === 'A' ? ' ncm-td--winner ncm-td--winnerA' : ''}`}>
                  {item.product_a_value !== null ? `${item.product_a_value} ${item.unit}` : '—'}
                </td>
                <td className={`ncm-td${item.winner === 'B' ? ' ncm-td--winner ncm-td--winnerB' : ''}`}>
                  {item.product_b_value !== null ? `${item.product_b_value} ${item.unit}` : '—'}
                </td>
                <td className="ncm-td">
                  {item.winner === 'A' && <span className="ncm-winnerTextA">✓ A</span>}
                  {item.winner === 'B' && <span className="ncm-winnerTextB">✓ B</span>}
                  {!item.winner && <span className="ncm-tieText">—</span>}
                  {item.difference_percent && item.winner && (
                    <span className="ncm-diffPercent">
                      {item.difference_percent}% {item.better_is === 'lower' ? 'less' : 'more'}
                    </span>
                  )}
                </td>
                {best_for_you?.per_metric && (
                  <td className="ncm-td ncm-tdBest4u">
                    {best_for_you.per_metric[item.metric] ? (
                      best_for_you.per_metric[item.metric].pick ? (
                        <span className={`ncm-best4uBadge ncm-best4uBadge--${best_for_you.per_metric[item.metric].pick === 'A' ? 'a' : 'b'}`}
                              title={best_for_you.per_metric[item.metric].reason}>
                          ✓ {best_for_you.per_metric[item.metric].pick}
                          <span className="ncm-best4uHint">{best_for_you.per_metric[item.metric].reason}</span>
                        </span>
                      ) : (
                        <span className="ncm-best4uBadge ncm-best4uBadge--neutral" title={best_for_you.per_metric[item.metric].reason}>—</span>
                      )
                    ) : (
                      <span className="ncm-best4uBadge ncm-best4uBadge--neutral">—</span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        <EdamamAttribution />

        {/* Personalized Recommendations */}
        {best_for_you?.recommendations?.length > 0 ? (
          <div className="ncm-recommendation">
            <div className="ncm-recTitle">
              Personalized for You
            </div>
            <ul className="ncm-recsSection">
              {best_for_you.recommendations.map((rec, idx) => (
                <li key={idx} className="ncm-recItem">{rec}</li>
              ))}
            </ul>
          </div>
        ) : personalized_recommendation ? (
          <div className="ncm-recommendation">
            <div className="ncm-recTitle">
              Personalized for You
            </div>
            <div className="ncm-recText">{personalized_recommendation}</div>
          </div>
        ) : null}

        {/* Choice Buttons */}
        <div className="ncm-choiceButtons">
          <div className="ncm-choiceLabel">
            Which did you choose?
          </div>
          <button
            onClick={() => recordChoice('A')}
            className={`ncm-choiceBtn ncm-choiceBtnA${comparisonResult.userChoice === 'A' ? ' ncm-choiceBtn--selected' : ''}`}
          >
            {comparisonResult.product_a?.name || 'Product A'}
          </button>
          <button
            onClick={() => recordChoice('B')}
            className={`ncm-choiceBtn ncm-choiceBtnB${comparisonResult.userChoice === 'B' ? ' ncm-choiceBtn--selected' : ''}`}
          >
            {comparisonResult.product_b?.name || 'Product B'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="ncm-overlay">
      <div className="ncm-modal">
        <div className="ncm-header">
          <h2 className="ncm-title">Nutrition Comparison</h2>
          <button className="ncm-closeBtn" onClick={onClose}>×</button>
        </div>

        <div className="ncm-content">
          {error && <div className="ncm-error">{error}</div>}

          {/* History Toggle */}
          <div className="ncm-historyToggle">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className={`ncm-historyBtn${showHistory ? ' ncm-historyBtn--active' : ''}`}
            >
              History ({history.length})
            </button>
            {history.length > 0 && (
              <button
                onClick={async () => {
                  if (await confirm({
                    title: 'Clear comparison history?',
                    message: `All ${history.length} saved comparison${history.length === 1 ? '' : 's'} will be deleted. This cannot be undone.`,
                    confirmLabel: 'Clear History',
                    destructive: true,
                  })) {
                    try {
                      for (const item of history) {
                        await fetchWithAuth(`${API_BASE}/api/compare/history/${item.id}`, { method: 'DELETE' });
                      }
                      setHistory([]);
                    } catch (err) {
                      setError('Failed to clear history');
                    }
                  }
                }}
                className="ncm-clearHistBtn"
              >
                Clear History
              </button>
            )}
            <button
              onClick={resetComparison}
              className="ncm-newCompBtn"
            >
              New Comparison
            </button>
          </div>

          {/* History Panel */}
          {showHistory && history.length > 0 && (
            <div className="ncm-historyList">
              <div className="ncm-historyHeading">Recent Comparisons</div>
              {history.map((item) => (
                <div
                  key={item.id}
                  className="ncm-historyItem"
                  onClick={() => {
                    setProductA({ name: item.product_a_name, upc: '', brand: '', nutrition: {} });
                    setProductB({ name: item.product_b_name, upc: '', brand: '', nutrition: {} });
                    setShowHistory(false);
                  }}
                >
                  <div>
                    <span className="ncm-histProductA">{item.product_a_name}</span>
                    <span className="ncm-histVs">vs</span>
                    <span className="ncm-histProductB">{item.product_b_name}</span>
                  </div>
                  <div className="ncm-histItemRight">
                    {item.winner && (
                      <span
                        className="ncm-histWinnerBadge"
                        style={{ backgroundColor: item.winner === 'A' ? '#DBEAFE' : '#FEF3C7', color: item.winner === 'A' ? '#1D4ED8' : '#B45309' }}
                      >
                        Winner: {item.winner}
                      </span>
                    )}
                    {item.user_choice && (
                      <span className="ncm-histChoiceBadge">
                        Chose: {item.user_choice}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Product Input Cards */}
          <div className="ncm-productsRow">
            {renderProductCard(productA, setProductA, 'A', true)}
            
            <button className="ncm-swapBtn" onClick={swapProducts} title="Swap products">
              ⇄
            </button>
            
            {renderProductCard(productB, setProductB, 'B', false)}
          </div>

          {/* Compare Button */}
          <button
            onClick={compareByUPC}
            disabled={comparing || !productA.upc || !productB.upc}
            className={`ncm-compareBtn${(comparing || !productA.upc || !productB.upc) ? ' ncm-compareBtn--disabled' : ''}`}
          >
            {comparing ? 'Comparing...' : 'Compare Products'}
          </button>

          {/* Results */}
          {renderResults()}

          <MedicalDisclaimer variant="health" />
        </div>
      </div>

      {/* Inline Camera Scanner Overlay (mobile only, same pattern as VGC) */}
      {isScanning && (
        <div className="ncm-scannerOverlay">
          <div className="ncm-scannerModal">
            <div className="ncm-scannerHeader">
              <h3 className="ncm-scannerTitle">Scan Barcode — Product {scanTarget}</h3>
              <button className="ncm-scannerClose" onClick={closeScanner}>×</button>
            </div>
            {scannerError ? (
              <div className="ncm-scannerErrorBox">
                <p>{scannerError}</p>
                <button className="btn btn-secondary btn-sm" onClick={closeScanner}>Close</button>
              </div>
            ) : (
              <>
                <div
                  id="ncm-scanner-host"
                  ref={hostElementRef}
                  className="ncm-scannerHost"
                />
                <p className="ncm-scannerHint">Point camera at barcode</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NutritionComparisonMode;